import { Checkbox, SegmentedControl } from "@mantine/core";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import AppLink from "@/components/atoms/AppLink";
import AppButton from "@/components/sitehub-ui/atoms/AppButton";
import { AppPasswordInput } from "@/components/sitehub-ui/atoms/AppPasswordInput";
import AppTextField from "@/components/sitehub-ui/atoms/AppTextField";
import { AppLandingTemplate } from "@/components/sitehub-ui/templates/AppLandingHorizontalTemplate";
import { PAGE_URL } from "@/data_source/constants";
import {
  isApiEnvironmentId,
  type ApiEnvironmentId
} from "@/data_source/constants/apiEnvironment";
import { AppLoadingStore } from "@/stores/app-loading-store";
import { classes } from "@/utils/css";
import { useObservable } from "@/utils/hook";

import styles from "./AppSignIn.module.css";

type Props = {
  email?: string;
  password?: string;
  remindMe?: boolean;
  errorMessage?: string;
  apiEnvironment?: ApiEnvironmentId;
  onApiEnvironmentChange?: (environment: ApiEnvironmentId) => void;
  onLoginClick?: () => void;
  onChange?: (
    ev: Partial<{ email: string; password: string; remindMe: boolean }>
  ) => void;
  showPassword?: boolean;
  togglePasswordVisibility?: () => void;
  handleRememberMeChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
};
export function AppSignInView(props: Props) {
  const { t } = useTranslation();
  const [isLoading] = useObservable(AppLoadingStore.getInstance().observable);
  return (
    <AppLandingTemplate
      title={t("Sign-in with your account")}
      message={t("Welcome back")}
      footer={
        <Grid
          container
          direction="column"
          alignItems="center"
          padding="42px 0px 0px 0px"
          gap={1}
        >
          <Typography
            className={classes(styles.label, styles.dark, styles.big)}
          >
            {t("Forgot your password ?")}
          </Typography>
          <Typography className={classes(styles.label)}>
            {t("Don't worry,")}
            <AppLink
              href={PAGE_URL.PASSWORD_RECOVERY}
              className={classes(styles.link)}
              data-testid="forgotPasswordButton"
            >
              {t("Click here")}
            </AppLink>
            {t("to restore it")}
          </Typography>
        </Grid>
      }
    >
      <Grid container direction="column" gap={2} padding="42px 0px 50px 0px">
        {props.apiEnvironment ? (
          <Grid container direction="column" rowGap={1}>
            <Grid item>
              <Typography className={styles.label} variant="inherit">
                {t("Environment")}
              </Typography>
            </Grid>
            <Grid item>
              <SegmentedControl
                fullWidth
                disabled={!!isLoading}
                value={props.apiEnvironment}
                onChange={(value) => {
                  if (!isApiEnvironmentId(value)) return;
                  props.onApiEnvironmentChange?.(value);
                }}
                data={[
                  { label: t("Development"), value: "development" },
                  { label: t("Staging"), value: "staging" }
                ]}
                data-testid="apiEnvironmentSelector"
              />
            </Grid>
          </Grid>
        ) : null}
        <Grid container direction="column" rowGap={1}>
          <Grid item>
            <Typography className={styles.label} variant="inherit">
              {t("Email")}
            </Typography>
          </Grid>
          <Grid item>
            <AppTextField
              disabled={!!isLoading}
              type="email"
              variant="outlined"
              value={props.email ?? ""}
              error={!props.email}
              helperText={t("Enter email")}
              onChange={(ev) =>
                props.onChange && props.onChange({ email: ev.target.value })
              }
              inputProps={{
                "data-testid": "inputEmail"
              }}
            />
          </Grid>
        </Grid>
        <Grid container direction="column" rowGap={1}>
          <Grid item>
            <Typography className={styles.label}>{t("Password")}</Typography>
          </Grid>
          <Grid item>
            <AppPasswordInput
              disabled={!!isLoading}
              variant="outlined"
              value={props.password ?? ""}
              error={!props.password}
              helperText={t("Enter password")}
              onChange={(ev) =>
                props.onChange && props.onChange({ password: ev.target.value })
              }
              onKeyUp={(ev) => {
                if (ev.key === "Enter") {
                  props.onLoginClick?.();
                }
              }}
              inputProps={{
                "data-testid": "inputPassword"
              }}
            />
          </Grid>
        </Grid>
        {props.errorMessage ? (
          <Grid item>
            <Typography className={classes(styles.label, styles.red)}>
              {t(props.errorMessage)}
            </Typography>
          </Grid>
        ) : null}
        <Grid container direction="row" justifyContent="space-between">
          <Checkbox
            label={
              <Typography className={classes(styles.label, styles.dark)}>
                {t("Remind me")}
              </Typography>
            }
            checked={props.remindMe ?? false}
            onChange={props.handleRememberMeChange}
            data-testid="checkRememberMe"
          />
          <AppButton
            label={t("Login")}
            disabled={!!isLoading}
            style={{ paddingLeft: "40px", paddingRight: "40px" }}
            onClick={() => props.onLoginClick && props.onLoginClick()}
            data-testid="loginButton"
          />
        </Grid>
      </Grid>
    </AppLandingTemplate>
  );
}
